import { router } from "@inertiajs/react";
import { Loader2Icon, RefreshCcwIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

interface FormActionButtonsProps {
	isLoading: boolean;
	hasChanges: boolean;
	pageURL: string;
	modifiedCount?: number;
	onResetAll?: () => void;
}

export function FormActionButtons({
	isLoading,
	hasChanges,
	pageURL,
	modifiedCount = 0,
	onResetAll,
}: FormActionButtonsProps) {
	const { t } = useTranslation("appointments-form");

	// Keep the vertical padding at md+ too: the bar is sticky over the visit list,
	// so without it the buttons sit flush against the content edge.
	return (
		<div className="sticky bottom-0 z-20 w-full border-t bg-card py-4 flex flex-col md:flex-row items-center justify-between gap-4">
			<div className="w-full md:w-auto text-sm text-muted-foreground/75">
				{modifiedCount > 0 ? (
					<span>
						<strong>{modifiedCount}</strong>{" "}
						{t("bulk_reschedule.summary.modified", "Modified")}
					</span>
				) : (
					<span>{t("tab.no_content.scheduled", "No changes to process")}</span>
				)}
			</div>

			<div className="flex flex-col md:flex-row flex-1 w-full items-stretch md:items-center justify-end gap-2">
				<Button
					variant="ghost"
					type="button"
					className="order-3 md:order-1 w-full md:w-auto"
					disabled={isLoading}
					onClick={() => {
						router.get(pageURL);
					}}
				>
					{t("button.cancel", "Cancel")}
				</Button>

				{onResetAll && (
					<AlertDialog>
						<AlertDialogTrigger asChild>
							<Button
								variant="ghost-destructive"
								type="button"
								className="order-2 w-full md:w-auto"
								disabled={!hasChanges || isLoading}
							>
								<RefreshCcwIcon className="h-4 w-4 shrink-0" />
								{t("bulk_reschedule.row.reset.label", "Reset All")}
							</Button>
						</AlertDialogTrigger>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>
									{t(
										"bulk_reschedule.dialogs.reset.title",
										"Reset all appointments?",
									)}
								</AlertDialogTitle>
								<AlertDialogDescription>
									{t("bulk_reschedule.dialogs.reset.description", {
										number: "all",
										defaultValue:
											"This will restore all appointments to their original date, time, and therapist. This action cannot be undone.",
									})}
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>
									{t("button.cancel", "Cancel")}
								</AlertDialogCancel>
								<AlertDialogAction onClick={onResetAll}>
									{t("bulk_reschedule.dialogs.reset.confirm", "Reset All")}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				)}

				<Button
					className="order-1 md:order-3 w-full md:w-auto"
					type="submit"
					disabled={!hasChanges || isLoading}
				>
					{isLoading ? (
						<>
							{t("button.save.loading", "Processing...")}
							<Loader2Icon className="h-4 w-4 animate-spin" />
						</>
					) : (
						t("bulk_reschedule.actions.save_changes", "Review Changes")
					)}
				</Button>
			</div>
		</div>
	);
}
